import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../hooks';
import { fetchOrganizationLimits, fetchActiveOverrides, fetchEnforcementHistory } from '../store/slices/limitsSlice';
import ScrollHeader from '../components/common/ScrollHeader';
import CreditPoolWidget from '../components/limits/CreditPoolWidget';
import OrganizationLimitsManager from '../components/limits/OrganizationLimitsManager';
import LoadingSpinner from '../components/common/LoadingSpinner';
import {
  ShieldCheckIcon,
  UsersIcon,
  BuildingOfficeIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';

const Limits: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);
  const { 
    organizationLimits, 
    activeOverrides, 
    enforcementHistory, 
    loading, 
    error 
  } = useAppSelector((state) => state.limits);

  const [selectedTab, setSelectedTab] = useState<'overview' | 'organizations' | 'overrides' | 'history'>('overview');

  useEffect(() => {
    // Load initial data
    dispatch(fetchActiveOverrides(undefined));
    dispatch(fetchEnforcementHistory({}));
  }, [dispatch]);

  const isSuperAdmin = user?.role === 'super_admin';

  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Access Denied
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            You need superadmin privileges to access the limits management system.
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ShieldCheckIcon },
    { id: 'organizations', name: 'Organizations', icon: BuildingOfficeIcon },
    { id: 'overrides', name: 'Active Overrides', icon: ExclamationTriangleIcon },
    { id: 'history', name: 'Enforcement History', icon: ClockIcon },
  ] as const;

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                  Organizations with Limits
                </dt>
                <dd className="text-lg font-medium text-gray-900 dark:text-white">
                  {Object.keys(organizationLimits).length}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-yellow-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                  Active Overrides
                </dt>
                <dd className="text-lg font-medium text-gray-900 dark:text-white">
                  {activeOverrides.length}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ClockIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                  Recent Violations
                </dt>
                <dd className="text-lg font-medium text-gray-900 dark:text-white">
                  {enforcementHistory.slice(0, 10).length}
                </dd>
              </dl>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
                  System Status
                </dt>
                <dd className="text-lg font-medium text-gray-900 dark:text-white">
                  Operational
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Credit Pool Widget */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CreditPoolWidget 
          title="Global Credit Pool Overview"
          showTopupButton={true}
        />
        
        <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Quick Actions
          </h3>
          <div className="space-y-3">
            <button
              onClick={() => setSelectedTab('organizations')}
              className="w-full flex items-center justify-between p-3 text-left bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Manage Organization Limits
              </span>
              <BuildingOfficeIcon className="w-4 h-4 text-gray-400" />
            </button>
            
            <button
              onClick={() => setSelectedTab('overrides')}
              className="w-full flex items-center justify-between p-3 text-left bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                View Active Overrides
              </span>
              <ExclamationTriangleIcon className="w-4 h-4 text-gray-400" />
            </button>
            
            <button
              onClick={() => setSelectedTab('history')}
              className="w-full flex items-center justify-between p-3 text-left bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                Review Enforcement History
              </span>
              <ClockIcon className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderOrganizations = () => (
    <OrganizationLimitsManager />
  );

  const renderOverrides = () => (
    <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Active Limit Overrides
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {activeOverrides.length} active
        </span>
      </div>
      
      {loading.overrides ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : activeOverrides.length === 0 ? (
        <div className="text-center py-12">
          <CheckCircleIcon className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            No active limit overrides
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {activeOverrides.map((override) => (
            <div
              key={override.id}
              className="p-4 border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/10 rounded-lg"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {override.user_email || `User ${override.user_id}`}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {override.org_name || `Organization ${override.org_id}`}
                  </p>
                  <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                    {override.override_reason}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Expires: {new Date(override.override_expires_at).toLocaleDateString()}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500">
                    Created: {new Date(override.created_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderHistory = () => (
    <div className="bg-white dark:bg-dark-surface rounded-xl border border-light-border dark:border-dark-border p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Enforcement History
        </h3>
        <span className="text-sm text-gray-500 dark:text-gray-400">
          Last 100 events
        </span>
      </div>
      
      {loading.enforcement ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : enforcementHistory.length === 0 ? (
        <div className="text-center py-12">
          <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            No enforcement history available
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {enforcementHistory.slice(0, 20).map((event) => (
            <div
              key={event.id}
              className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    {event.user_email || `User ${event.user_id}`}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {event.org_name || `Organization ${event.org_id}`}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                    {event.limit_type} - {event.action_taken}: {event.reason}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(event.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderContent = () => {
    switch (selectedTab) {
      case 'overview':
        return renderOverview();
      case 'organizations':
        return renderOrganizations();
      case 'overrides':
        return renderOverrides();
      case 'history':
        return renderHistory();
      default:
        return renderOverview();
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <ScrollHeader title="Limits Management" />
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          Manage credit limits, feature access, and monitor enforcement across all organizations
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const isActive = selectedTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id)}
                className={`group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm ${
                  isActive
                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                <tab.icon
                  className={`-ml-0.5 mr-2 h-5 w-5 ${
                    isActive
                      ? 'text-primary-500 dark:text-primary-400'
                      : 'text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                  }`}
                />
                {tab.name}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Content */}
      {renderContent()}
    </div>
  );
};

export default Limits;
